import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

interface Crumb {
  label: string
  to?: string
}

interface DocBreadcrumbsProps {
  crumbs: Crumb[]
}

/**
 * Lightweight breadcrumb bar for the documentation portal.
 * The last crumb is the current page and is not a link.
 *
 * Accessibility: uses nav + aria-label="Breadcrumb" with aria-current="page"
 * on the terminal crumb per ARIA authoring practices.
 */
export function DocBreadcrumbs({ crumbs }: DocBreadcrumbsProps) {
  if (crumbs.length === 0) return null

  return (
    <nav aria-label="Breadcrumb" className="mb-4">
      <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-fg">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1
          return (
            <li key={crumb.label} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={10} aria-hidden className="shrink-0" />}
              {isLast || !crumb.to ? (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={isLast ? 'text-fg' : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  className="hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:rounded"
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
